import { expectedReturnsQuerySchema, type ExpectedReturnsQuery, type Page } from '@chukua/common';
import { useEffect, useState } from 'react';
import { useListQuery } from '../../../shared/hooks/use-list-query';
import { toListParams } from '../../../shared/policies/list-params';
import { ApiError } from '../../../shared/shared.api';
import { listExpectedReturns, type ExpectedReturnItem } from '../yard.api';

/** `queryKey` records which query the loaded page answers, so a newer query shows as refreshing over the old rows. */
export type ExpectedReturnsState =
  | { status: 'loading' }
  | { status: 'loaded'; page: Page<ExpectedReturnItem>; queryKey: string }
  | { status: 'forbidden' }
  | { status: 'error'; error: unknown };

const keyOf = (query: ExpectedReturnsQuery) => toListParams(query).toString();

/** The page of trucks due back that the URL's list query asks for, re-fetched whenever that query changes. */
export function useExpectedReturns() {
  const list = useListQuery(expectedReturnsQuerySchema);
  const { query } = list;
  const [state, setState] = useState<ExpectedReturnsState>({ status: 'loading' });

  useEffect(() => {
    let cancelled = false;
    listExpectedReturns(query)
      .then((page) => {
        if (!cancelled) setState({ status: 'loaded', page, queryKey: keyOf(query) });
      })
      .catch((error) => {
        if (!cancelled) setState(error instanceof ApiError && error.status === 403 ? { status: 'forbidden' } : { status: 'error', error });
      });
    return () => {
      cancelled = true;
    };
  }, [query]);

  const isRefreshing = state.status === 'loaded' && state.queryKey !== keyOf(query);
  return { state, list, isRefreshing };
}
